function sanitizeDatePart(date: string): string {
  const trimmed = String(date || '').trim();
  if (!trimmed || trimmed === '[object Object]') return '';
  return trimmed;
}

export function serializeDateFilter(operator: string, date: string): string {
  const safeDate = sanitizeDatePart(date);
  if (!safeDate) return '';
  return operator === 'before' ? `[Before]${safeDate}` : `[After]${safeDate}`;
}

export function parseDateFilterValue(value: string) {
  if (!value) return { mode: 'all' as const, operator: 'after', date: '' };
  if (value === '[Empty]' || value === '[Not Empty]') {
    return { mode: 'special' as const, operator: 'after', date: '', special: value };
  }
  if (value.startsWith('[Before]')) {
    return { mode: 'date' as const, operator: 'before', date: sanitizeDatePart(value.slice(8)) };
  }
  if (value.startsWith('[After]')) {
    return { mode: 'date' as const, operator: 'after', date: sanitizeDatePart(value.slice(7)) };
  }
  return { mode: 'all' as const, operator: 'after', date: '' };
}

/**
 * DateTimePicker clear emits `{ target: { value: null } }`. Using
 * `String(event.target.value || event)` would become "[object Object]".
 */
export function extractDateFilterInput(dateValue: unknown): string {
  if (dateValue == null) return '';

  if (typeof dateValue === 'object' && dateValue !== null && 'target' in dateValue) {
    const raw = (dateValue as { target?: { value?: unknown } }).target?.value;
    if (raw == null) return '';
    if (typeof raw === 'object') return '';
    return String(raw).trim();
  }

  if (typeof dateValue === 'object') return '';
  return String(dateValue).trim();
}

export type DateFilterUiOperator = 'before' | 'after' | 'present' | 'empty';

export function getDateFilterUiOperator(value: string): DateFilterUiOperator {
  const parsed = parseDateFilterValue(value);
  if (parsed.mode === 'special') {
    return parsed.special === '[Empty]' ? 'empty' : 'present';
  }
  if (parsed.mode === 'date') {
    return parsed.operator === 'before' ? 'before' : 'after';
  }
  return 'after';
}

export function applyDateFilterUiOperator(
  uiOperator: DateFilterUiOperator,
  previousValue: string
): string {
  if (uiOperator === 'present') return '[Not Empty]';
  if (uiOperator === 'empty') return '[Empty]';

  const parsed = parseDateFilterValue(previousValue);
  const date = parsed.mode === 'date' ? parsed.date : '';
  return date ? serializeDateFilter(uiOperator, date) : '';
}

export function dateFilterNeedsPicker(uiOperator: DateFilterUiOperator): boolean {
  return uiOperator === 'before' || uiOperator === 'after';
}
